import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { API_URL } from '../lib/api-url';
import { getApiErrorMessage } from '../lib/api-error';
import { installOfflineInterceptors } from '../offline/interceptors';
import { scheduleSync } from '../offline/sync-scheduler';

declare module 'axios' {
  export interface AxiosRequestConfig {
    background?: boolean;
    skipGlobalErrorToast?: boolean;
    /** Skip offline-first layer (used by sync replay and background revalidation). */
    skipOffline?: boolean;
    /** Force network fetch, bypassing local cache (e.g. pull-to-refresh). */
    skipCache?: boolean;
    /** Response was served from local store; skip writing back to cache. */
    servedFromLocal?: boolean;
  }
}

const API_REQUEST_TIMEOUT_MS = 30_000;

const client = axios.create({
  baseURL: API_URL,
  timeout: API_REQUEST_TIMEOUT_MS,
});

installOfflineInterceptors(client, { scheduleSync });

let apiErrorToast: ((message: string) => void) | null = null;

export function registerApiErrorToast(handler: ((message: string) => void) | null) {
  apiErrorToast = handler;
}

function isAuthError(error: AxiosError): boolean {
  const status = error.response?.status;
  return status === 401 || status === 403;
}

function shouldShowBackgroundToast(error: AxiosError): boolean {
  const config = error.config;
  if (!config?.background || config.skipGlobalErrorToast || isAuthError(error)) {
    return false;
  }
  return true;
}

client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error) && shouldShowBackgroundToast(error)) {
      apiErrorToast?.(getApiErrorMessage(error));
    }
    return Promise.reject(error);
  },
);

export default client;
